import { Icon } from '@iconify/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import {
  COLLABORATOR_PERMISSIONS,
  createCollaborator,
  deleteCollaborator,
  listCollaborators,
  updateCollaborator,
  type CollaboratorInput,
} from '@/lib/api/collaborators.service'
import {
  getCollaboratorOperationError,
  type CollaboratorFormField,
} from '@/lib/collaborator-errors'
import type { CollaboratorPermission, InstitutionCollaborator } from '@/types/api'
import styles from './CollaboratorsPage.module.css'

const EMPTY_FORM: CollaboratorInput = {
  name: '',
  email: '',
  password: '',
  permissions: [],
  is_active: true,
}

function permissionList(row: InstitutionCollaborator): CollaboratorPermission[] {
  return Array.isArray(row.permissions) ? row.permissions : []
}

export function CollaboratorsPage() {
  const { isCollaborator } = useAuth()
  const [rows, setRows] = useState<InstitutionCollaborator[]>([])
  const [form, setForm] = useState<CollaboratorInput>(EMPTY_FORM)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeletingId, setIsDeletingId] = useState<number | null>(null)
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<CollaboratorFormField, string>>
  >({})

  const isEditing = editingId !== null
  const isBusy = isSaving || isDeletingId !== null

  const load = useCallback(async (options?: { silent?: boolean }) => {
    if (isCollaborator) {
      setIsLoading(false)
      return
    }

    if (!options?.silent) {
      setIsLoading(true)
    }
    try {
      setRows(await listCollaborators())
    } catch (err) {
      const mapped = getCollaboratorOperationError(err)
      setError(mapped.message)
      if (!options?.silent) {
        setRows([])
      }
    } finally {
      setIsLoading(false)
    }
  }, [isCollaborator])

  useEffect(() => {
    void load()
  }, [load])

  const selectedPermissionLabels = useMemo(() => {
    const labels = new Map(
      COLLABORATOR_PERMISSIONS.map((item) => [item.value, item.label])
    )
    return rows.reduce<Record<number, string>>((acc, row) => {
      acc[row.id] = permissionList(row)
        .map((permission) => labels.get(permission) ?? permission)
        .join(', ')
      return acc
    }, {})
  }, [rows])

  function clearFeedback() {
    setError(null)
    setSuccess(null)
    setFieldErrors({})
  }

  function resetForm() {
    setEditingId(null)
    setForm(EMPTY_FORM)
    setFieldErrors({})
  }

  function updateField<K extends keyof CollaboratorInput>(key: K, value: CollaboratorInput[K]) {
    setForm((current) => ({ ...current, [key]: value }))
    if (key === 'name' || key === 'email' || key === 'password') {
      setFieldErrors((current) => {
        if (!current[key]) return current
        const next = { ...current }
        delete next[key]
        return next
      })
    }
  }

  function togglePermission(permission: CollaboratorPermission) {
    setForm((current) => {
      const exists = current.permissions.includes(permission)
      return {
        ...current,
        permissions: exists
          ? current.permissions.filter((item) => item !== permission)
          : [...current.permissions, permission],
      }
    })
    setFieldErrors((current) => {
      if (!current.permissions) return current
      const next = { ...current }
      delete next.permissions
      return next
    })
  }

  function startEdit(collaborator: InstitutionCollaborator) {
    setEditingId(collaborator.id)
    setForm({
      name: collaborator.name,
      email: collaborator.email,
      password: '',
      permissions: permissionList(collaborator),
      is_active: collaborator.is_active,
    })
    setPendingDeleteId(null)
    clearFeedback()
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setIsSaving(true)
    clearFeedback()

    try {
      if (isEditing && editingId !== null) {
        const payload: Partial<CollaboratorInput> = {
          name: form.name.trim(),
          email: form.email.trim(),
          permissions: form.permissions,
          is_active: form.is_active,
        }
        if (form.password?.trim()) {
          payload.password = form.password
        }
        await updateCollaborator(editingId, payload)
        setSuccess('Colaborador atualizado com sucesso.')
      } else {
        await createCollaborator({
          ...form,
          name: form.name.trim(),
          email: form.email.trim(),
        })
        setSuccess('Colaborador criado com sucesso.')
      }

      resetForm()
      await load({ silent: true })
    } catch (err) {
      const mapped = getCollaboratorOperationError(err)
      setError(mapped.message)
      setFieldErrors(mapped.fieldErrors)
    } finally {
      setIsSaving(false)
    }
  }

  async function handleDelete(collaborator: InstitutionCollaborator) {
    setIsDeletingId(collaborator.id)
    clearFeedback()
    try {
      await deleteCollaborator(collaborator.id)
      setSuccess('Colaborador excluído com sucesso.')
      setPendingDeleteId(null)
      if (editingId === collaborator.id) {
        resetForm()
      }
      await load({ silent: true })
    } catch (err) {
      const mapped = getCollaboratorOperationError(err)
      setError(mapped.message)
    } finally {
      setIsDeletingId(null)
    }
  }

  if (isCollaborator) {
    return (
      <div className={styles.page}>
        <div className={styles.centered}>
          <Icon icon="mdi:lock-outline" className={styles.emptyIcon} />
          Apenas a instituição dona pode gerenciar colaboradores.
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Colaboradores</h1>
          <p className={styles.subtitle}>
            Cadastre acessos com permissões específicas para o painel da instituição.
          </p>
        </div>
      </div>

      {(error || success) && (
        <div className={error ? styles.alertError : styles.alertSuccess} role="alert">
          {error || success}
        </div>
      )}

      <form className={styles.formCard} onSubmit={handleSubmit}>
        <div className={styles.formHeader}>
          <strong>{isEditing ? 'Editar colaborador' : 'Novo colaborador'}</strong>
          {isEditing && (
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={resetForm}
              disabled={isBusy}
            >
              Cancelar edição
            </button>
          )}
        </div>

        <div className={styles.formGrid}>
          <div className={styles.field}>
            <label htmlFor="collaborator-name">Nome</label>
            <input
              id="collaborator-name"
              className={`${styles.input} ${fieldErrors.name ? styles.inputInvalid : ''}`}
              value={form.name}
              onChange={(event) => updateField('name', event.target.value)}
              aria-invalid={Boolean(fieldErrors.name)}
              aria-describedby={fieldErrors.name ? 'collaborator-name-error' : undefined}
              required
              disabled={isBusy}
            />
            {fieldErrors.name && (
              <span id="collaborator-name-error" className={styles.fieldError}>
                {fieldErrors.name}
              </span>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="collaborator-email">E-mail</label>
            <input
              id="collaborator-email"
              className={`${styles.input} ${fieldErrors.email ? styles.inputInvalid : ''}`}
              type="email"
              value={form.email}
              onChange={(event) => updateField('email', event.target.value)}
              aria-invalid={Boolean(fieldErrors.email)}
              aria-describedby={
                fieldErrors.email ? 'collaborator-email-error' : 'collaborator-email-hint'
              }
              required
              disabled={isBusy}
              autoComplete="off"
            />
            {fieldErrors.email ? (
              <span id="collaborator-email-error" className={styles.fieldError}>
                {fieldErrors.email}
              </span>
            ) : (
              <span id="collaborator-email-hint" className={styles.fieldHint}>
                Este e-mail será o login do colaborador. Não use o e-mail da instituição
                nem um e-mail que já tenha acesso na plataforma.
              </span>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="collaborator-password">
              Senha {isEditing ? '(opcional)' : ''}
            </label>
            <input
              id="collaborator-password"
              className={`${styles.input} ${fieldErrors.password ? styles.inputInvalid : ''}`}
              type="password"
              minLength={8}
              value={form.password ?? ''}
              onChange={(event) => updateField('password', event.target.value)}
              aria-invalid={Boolean(fieldErrors.password)}
              aria-describedby={
                fieldErrors.password ? 'collaborator-password-error' : undefined
              }
              required={!isEditing}
              disabled={isBusy}
              autoComplete="new-password"
            />
            {fieldErrors.password && (
              <span id="collaborator-password-error" className={styles.fieldError}>
                {fieldErrors.password}
              </span>
            )}
          </div>

          <label className={styles.checkboxLine}>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(event) => updateField('is_active', event.target.checked)}
              disabled={isBusy}
            />
            Colaborador ativo
          </label>
        </div>

        <fieldset className={styles.permissions} disabled={isBusy}>
          <legend>Permissões</legend>
          {COLLABORATOR_PERMISSIONS.map((permission) => (
            <label key={permission.value} className={styles.permissionItem}>
              <input
                type="checkbox"
                checked={form.permissions.includes(permission.value)}
                onChange={() => togglePermission(permission.value)}
              />
              <span className={styles.permissionCopy}>
                <span>{permission.label}</span>
                {permission.description ? (
                  <span className={styles.permissionHint}>{permission.description}</span>
                ) : null}
              </span>
            </label>
          ))}
          {fieldErrors.permissions && (
            <span className={styles.fieldError}>{fieldErrors.permissions}</span>
          )}
        </fieldset>

        <button type="submit" className={styles.primaryButton} disabled={isBusy}>
          {isSaving
            ? 'Salvando...'
            : isEditing
              ? 'Salvar alterações'
              : 'Criar colaborador'}
        </button>
      </form>

      {isLoading ? (
        <div className={styles.centered}>
          <Icon icon="mdi:loading" className={styles.spinner} />
          Carregando colaboradores...
        </div>
      ) : rows.length === 0 ? (
        <div className={styles.centered}>
          <Icon icon="mdi:account-group-outline" className={styles.emptyIcon} />
          Nenhum colaborador cadastrado.
        </div>
      ) : (
        <div className={styles.tableCard}>
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>E-mail</th>
                  <th>Permissões</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.name}</td>
                    <td>{row.email}</td>
                    <td>{selectedPermissionLabels[row.id] || 'Sem permissões'}</td>
                    <td>{row.is_active ? 'Ativo' : 'Inativo'}</td>
                    <td>
                      {pendingDeleteId === row.id ? (
                        <div className={styles.deleteConfirm}>
                          <span>Excluir {row.name}?</span>
                          <button
                            type="button"
                            className={styles.dangerButton}
                            disabled={isBusy}
                            onClick={() => void handleDelete(row)}
                          >
                            {isDeletingId === row.id ? 'Excluindo...' : 'Confirmar'}
                          </button>
                          <button
                            type="button"
                            className={styles.secondaryButton}
                            disabled={isBusy}
                            onClick={() => setPendingDeleteId(null)}
                          >
                            Cancelar
                          </button>
                        </div>
                      ) : (
                        <div className={styles.actions}>
                          <button
                            type="button"
                            className={styles.linkButton}
                            disabled={isBusy}
                            onClick={() => startEdit(row)}
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            className={styles.dangerButton}
                            disabled={isBusy}
                            onClick={() => setPendingDeleteId(row.id)}
                          >
                            Excluir
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
